import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Lightbulb, X, RotateCcw, Layers, Scale, Shirt, Zap, ChevronDown, ChevronRight, Backpack, BaggageClaim } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PackingTip {
  id: string;
  title: string;
  content: string;
  category: 'space' | 'weight' | 'organization' | 'security' | 'comfort' | 'method';
  icon: React.ReactNode;
  relevant: boolean;
}

interface PackingTipsProps {
  tripType: string;
  destination: string;
  tripLength: number;
  luggageConfig?: string[];
}

export function PackingTips({ tripType, destination, tripLength, luggageConfig = [] }: PackingTipsProps) {
  // Determine luggage configuration
  const isCarryOnOnly = luggageConfig.includes('carry-on') && !luggageConfig.includes('checked');
  const hasBackpack = luggageConfig.includes('backpack');
  const isMinimalPacking = isCarryOnOnly || (hasBackpack && luggageConfig.length === 1);
  const hasCheckedBag = luggageConfig.includes('checked');
  const [dismissedTips, setDismissedTips] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const allTips: PackingTip[] = [
    // PACKING METHODS - Show first for minimal packing
    {
      id: '333-method',
      title: 'The 3-3-3 Method',
      content: 'Pack 3 tops, 3 bottoms, and 3 pairs of shoes. This versatile formula creates 9+ outfit combinations while keeping your bag light.',
      category: 'method',
      icon: <Layers className="h-4 w-4" />,
      relevant: isMinimalPacking || tripLength <= 7
    },
    {
      id: '54321-method',
      title: 'The 5-4-3-2-1 Method',
      content: 'Pack 5 sets of underwear/socks, 4 tops, 3 bottoms, 2 pairs of shoes, and 1 hat/jacket. Perfect for a week-long trip.',
      category: 'method',
      icon: <Layers className="h-4 w-4" />,
      relevant: tripLength >= 5 && tripLength <= 10
    },
    {
      id: 'capsule-wardrobe',
      title: 'Create a Capsule Wardrobe',
      content: 'Choose a color palette (e.g., black, white, navy) so every piece mixes and matches. Fewer items, more outfits.',
      category: 'method',
      icon: <Shirt className="h-4 w-4" />,
      relevant: isMinimalPacking || tripLength > 5
    },
    // CARRY-ON ONLY TIPS
    {
      id: 'carryon-compression',
      title: 'Compression Bags Are Essential',
      content: 'For carry-on only travel, compression bags can reduce clothing volume by 50%. Roll clothes first, then compress.',
      category: 'space',
      icon: <BaggageClaim className="h-4 w-4" />,
      relevant: isCarryOnOnly
    },
    {
      id: 'carryon-versatile-shoes',
      title: 'One Versatile Shoe Rule',
      content: 'With carry-on only, limit yourself to 1-2 pairs of shoes. Choose neutral colors that work for day and night.',
      category: 'space',
      icon: <BaggageClaim className="h-4 w-4" />,
      relevant: isCarryOnOnly
    },
    {
      id: 'carryon-laundry',
      title: 'Plan for Laundry',
      content: 'Pack a small laundry kit (sink stopper, travel detergent). Washing clothes mid-trip lets you pack half the clothing.',
      category: 'organization',
      icon: <RotateCcw className="h-4 w-4" />,
      relevant: isCarryOnOnly && tripLength > 4
    },
    {
      id: 'carryon-layers',
      title: 'Layer Up on Flight Day',
      content: 'Wear your bulkiest items (jacket, boots, sweater) on the plane. This alone can save 2-3kg of luggage space.',
      category: 'weight',
      icon: <BaggageClaim className="h-4 w-4" />,
      relevant: isCarryOnOnly
    },
    // BACKPACK TIPS
    {
      id: 'backpack-weight-distribution',
      title: 'Backpack Weight Balance',
      content: 'Pack heavy items close to your back and centered. Light items go at the bottom, medium-weight items on top.',
      category: 'weight',
      icon: <Backpack className="h-4 w-4" />,
      relevant: hasBackpack
    },
    {
      id: 'backpack-accessibility',
      title: 'Pack for Accessibility',
      content: 'Keep frequently needed items (passport, phone charger, snacks) in outer pockets or top of the bag for easy access.',
      category: 'organization',
      icon: <Backpack className="h-4 w-4" />,
      relevant: hasBackpack
    },
    {
      id: 'backpack-rain-cover',
      title: "Don't Forget Rain Protection",
      content: 'Pack a lightweight rain cover for your backpack. It also doubles as dust protection and adds a security layer.',
      category: 'security',
      icon: <Backpack className="h-4 w-4" />,
      relevant: hasBackpack
    },
    {
      id: 'backpack-daypack',
      title: 'Pack a Packable Daypack',
      content: 'Bring a lightweight foldable daypack for day trips. It packs flat but gives you freedom to explore without your main bag.',
      category: 'organization',
      icon: <Backpack className="h-4 w-4" />,
      relevant: hasBackpack
    },
    // GENERAL TIPS
    {
      id: 'roll-clothes',
      title: 'Roll Your Clothes',
      content: 'Rolling clothes instead of folding saves 30% more space in your luggage and reduces wrinkles.',
      category: 'space',
      icon: <RotateCcw className="h-4 w-4" />,
      relevant: true
    },
    {
      id: 'packing-cubes',
      title: 'Use Packing Cubes',
      content: 'Packing cubes help organize your luggage and make unpacking a breeze. Group similar items together.',
      category: 'organization',
      icon: <Layers className="h-4 w-4" />,
      relevant: tripLength > 3
    },
    {
      id: 'heavy-items',
      title: 'Pack Heavy Items First',
      content: 'Place heavier items at the bottom of your suitcase for better weight distribution and easier handling.',
      category: 'weight',
      icon: <Scale className="h-4 w-4" />,
      relevant: hasCheckedBag
    },
    {
      id: 'outfit-planning',
      title: 'Plan Your Outfits',
      content: 'Plan outfits in advance to avoid overpacking. Choose versatile pieces that mix and match.',
      category: 'organization',
      icon: <Shirt className="h-4 w-4" />,
      relevant: tripLength > 5 && !isMinimalPacking
    },
    {
      id: 'charger-organization',
      title: 'Bundle Your Cables',
      content: 'Use cable organizers or rubber bands to keep charging cables tidy and easy to find.',
      category: 'organization',
      icon: <Zap className="h-4 w-4" />,
      relevant: true
    },
    {
      id: 'layer-strategy',
      title: 'Wear Heavy Items',
      content: 'Wear your heaviest shoes and jacket on the plane to save luggage space and weight.',
      category: 'weight',
      icon: <Shirt className="h-4 w-4" />,
      relevant: !isCarryOnOnly // Already covered in carryon-layers
    },
    {
      id: 'business-wrinkles',
      title: 'Prevent Wrinkles',
      content: 'For business trips, pack dress shirts in dry cleaning bags to prevent wrinkles.',
      category: 'organization',
      icon: <Shirt className="h-4 w-4" />,
      relevant: tripType === 'business'
    },
    {
      id: 'adventure-quick-dry',
      title: 'Choose Quick-Dry Fabrics',
      content: 'For adventure trips, pack moisture-wicking and quick-dry clothing to stay comfortable.',
      category: 'comfort',
      icon: <RotateCcw className="h-4 w-4" />,
      relevant: tripType === 'adventure'
    },
    {
      id: 'beach-waterproof',
      title: 'Waterproof Your Valuables',
      content: 'Use waterproof bags for electronics and important documents when going to beach destinations.',
      category: 'security',
      icon: <Zap className="h-4 w-4" />,
      relevant: tripType === 'beach' || destination.toLowerCase().includes('beach')
    },
    {
      id: 'shoes-stuffing',
      title: 'Stuff Your Shoes',
      content: 'Use the space inside shoes to pack socks, underwear, or chargers. Wrap shoes in shower caps to protect other items.',
      category: 'space',
      icon: <Layers className="h-4 w-4" />,
      relevant: !isMinimalPacking
    },
    {
      id: 'liquid-limits',
      title: 'Mind the Liquid Limits',
      content: 'For carry-on bags, remember the 3-1-1 rule: 3.4oz (100ml) containers, 1 quart bag, 1 bag per passenger.',
      category: 'security',
      icon: <Scale className="h-4 w-4" />,
      relevant: luggageConfig.includes('carry-on') || luggageConfig.includes('personal')
    },
    {
      id: 'backup-copies',
      title: 'Make Document Copies',
      content: 'Keep digital and physical copies of important documents in separate locations.',
      category: 'security',
      icon: <Layers className="h-4 w-4" />,
      relevant: true
    }
  ];

  const relevantTips = allTips.filter(tip => tip.relevant && !dismissedTips.has(tip.id));
  const displayedTips = showAll ? relevantTips : relevantTips.slice(0, 3);

  const dismissTip = (tipId: string) => {
    setDismissedTips(prev => new Set([...prev, tipId]));
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'space': return 'bg-cyan-500';
      case 'weight': return 'bg-green-500';
      case 'organization': return 'bg-cyan-500';
      case 'security': return 'bg-red-500';
      case 'comfort': return 'bg-yellow-500';
      case 'method': return 'bg-amber-500';
      default: return 'bg-muted';
    }
  };

  const getCategoryName = (category: string) => {
    switch (category) {
      case 'space': return 'Space Saving';
      case 'weight': return 'Weight Management';
      case 'organization': return 'Organization';
      case 'security': return 'Security';
      case 'comfort': return 'Comfort';
      case 'method': return 'Packing Method';
      default: return 'General';
    }
  };

  if (relevantTips.length === 0) {
    return null;
  }

  return (
    <Card className="border-cyan-500/20 shadow-md shadow-cyan-500/5 overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 sm:pb-4 cursor-pointer transition-colors bg-gradient-to-br from-cyan-50/60 to-transparent dark:from-cyan-950/15 dark:to-transparent hover:from-cyan-50 dark:hover:from-cyan-950/25">
            <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-sm shadow-cyan-500/20">
                <Lightbulb className="h-4 w-4" />
              </span>
              Packing Tips
              {isOpen ? (
                <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
              )}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm pl-11">
              Smart tips to make your packing more efficient
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-3 sm:space-y-4">
            {displayedTips.map(tip => (
              <div
                key={tip.id}
                className="relative p-3 sm:p-4 border border-border rounded-lg bg-card hover:bg-muted/30 transition-colors"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => dismissTip(tip.id)}
                  className="absolute top-1 right-1 sm:top-2 sm:right-2 h-7 w-7 sm:h-6 sm:w-6 p-0 text-muted-foreground hover:text-foreground touch-manipulation"
                >
                  <X className="h-3 w-3" />
                </Button>
                
                <div className="space-y-2 sm:space-y-3 pr-6 sm:pr-8">
                  <div className="flex items-start sm:items-center gap-2 sm:gap-3">
                    <div className={cn("p-1.5 sm:p-2 rounded-lg text-white flex-shrink-0", getCategoryColor(tip.category))}>
                      {tip.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-foreground text-sm sm:text-base leading-tight mb-1">{tip.title}</h4>
                      <Badge variant="outline" className="text-xs">
                        {getCategoryName(tip.category)}
                      </Badge>
                    </div>
                  </div>
                  
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
                    {tip.content}
                  </p>
                </div>
              </div>
            ))}

            {relevantTips.length > 3 && (
              <Button
                variant="outline"
                onClick={() => setShowAll(!showAll)}
                className="w-full touch-manipulation py-3 sm:py-2"
                size="sm"
              >
                {showAll ? 'Show Less' : `Show ${relevantTips.length - 3} More Tips`}
              </Button>
            )}

            {dismissedTips.size > 0 && (
              <Button
                variant="ghost"
                onClick={() => setDismissedTips(new Set())}
                className="w-full text-muted-foreground touch-manipulation py-3 sm:py-2"
                size="sm"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Restore dismissed tips
              </Button>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}